/**
 * Utility functions for calculating accommodation markup with proper typing
 */

import {
  BatchAvailabilityResponse,
  DayAvailability,
  DayRoomAvailability,
  MinAvailabilityItem,
} from "../types";

export interface MarkupSettings {
  accommodationMarkupType: "percentage" | "amount";
  accommodationMarkupValue: number;
  /** Weekend surcharge in percent of the base price, applied to Friday and Saturday nights only. */
  weekendSurchargePercent?: number;
}

/**
 * Converts a numeric-like value (number, decimal string from the API,
 * undefined, null) to a finite number. Anything unusable becomes 0.
 */
export const toFiniteNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Day of week (0 = Sunday) of the calendar date at the start of `date`
 * (`YYYY-MM-DD` or an ISO timestamp). Uses Date.UTC + getUTCDay only, so the
 * result does not depend on the process time zone. Returns null when no date can be read.
 */
const getCalendarDayOfWeek = (date: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || "");
  if (!match) return null;
  const day = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  ).getUTCDay();
  return Number.isNaN(day) ? null : day;
};

/**
 * Price of one accommodation night: base price with global markup and the
 * weekend surcharge (Friday and Saturday nights only).
 *
 * - no markup: the base price is returned untouched
 * - percentage: round(base * (1 + (markup + surcharge) / 100))
 * - amount: round(base + amount + base * surcharge / 100)
 * - once markup is set the result is always rounded
 *
 * @param basePrice net base price of the night
 * @param nightDate calendar date of the night (`YYYY-MM-DD` or ISO timestamp)
 * @param markup markup settings (values may arrive as decimal strings)
 */
export function calculateNightPriceWithMarkup(
  basePrice: number,
  nightDate: string,
  markup: MarkupSettings | null | undefined
): number {
  if (!markup) return basePrice;

  const value = toFiniteNumber(markup.accommodationMarkupValue);
  const weekday = getCalendarDayOfWeek(nightDate);
  const surcharge =
    weekday === 5 || weekday === 6
      ? toFiniteNumber(markup.weekendSurchargePercent)
      : 0;

  if (markup.accommodationMarkupType === "amount") {
    return Math.round(basePrice + value + (basePrice * surcharge) / 100);
  }
  return Math.round(basePrice * (1 + (value + surcharge) / 100));
}

/**
 * Whether the Saturday-to-Sunday pricing strategy may be offered: only when
 * neither the global markup nor the weekend surcharge is set.
 */
export function shouldOfferWeekendStrategy(
  markup: MarkupSettings | null | undefined
): boolean {
  if (!markup) return true;
  return (
    toFiniteNumber(markup.accommodationMarkupValue) === 0 &&
    toFiniteNumber(markup.weekendSurchargePercent) === 0
  );
}

/**
 * Calculate price with markup applied
 *
 * @deprecated Ignores the weekend surcharge and treats markup 0 as "no markup"
 * (returns the unrounded base price). For accommodation night prices use
 * {@link calculateNightPriceWithMarkup}.
 */
export function calculatePriceWithMarkup(
  basePrice: number,
  markup: MarkupSettings | null
): number {
  if (!markup || !markup.accommodationMarkupValue) {
    return basePrice;
  }

  if (markup.accommodationMarkupType === "percentage") {
    const multiplier = 1 + markup.accommodationMarkupValue / 100;
    return Math.round(basePrice * multiplier);
  }

  // For future - amount-based markup
  return Math.round(basePrice + markup.accommodationMarkupValue);
}

/**
 * Apply markup to price object (single/double/triple pricing)
 */
export function applyMarkupToPriceObject(
  priceObj: { net: number; gross: number } | undefined,
  markup: MarkupSettings | null
): { net: number; gross: number; originalNet?: number } | undefined {
  if (!priceObj || !markup) return priceObj;

  const originalNet = priceObj.net;
  const newNet = calculatePriceWithMarkup(originalNet, markup);

  // Calculate new gross maintaining VAT rate
  const vatRate = (priceObj.gross - priceObj.net) / priceObj.net;
  const newGross = Math.round(newNet * (1 + vatRate));

  return {
    net: newNet,
    gross: newGross,
    originalNet,
  };
}

/**
 * Apply markup to daily pricing structure (per date)
 */
export function applyMarkupToDayPricing(
  dayPricing: {
    single?: { net: number; gross: number };
    double?: { net: number; gross: number };
    triple?: { net: number; gross: number };
  },
  markup: MarkupSettings | null
): {
  single?: { net: number; gross: number; originalNet?: number };
  double?: { net: number; gross: number; originalNet?: number };
  triple?: { net: number; gross: number; originalNet?: number };
} {
  if (!markup) return dayPricing;

  return {
    single: applyMarkupToPriceObject(dayPricing.single, markup),
    double: applyMarkupToPriceObject(dayPricing.double, markup),
    triple: applyMarkupToPriceObject(dayPricing.triple, markup),
  };
}

/**
 * Apply markup to MinAvailabilityItem pricing structure
 */
export function applyMarkupToMinAvailabilityItem(
  minItem: MinAvailabilityItem,
  markup: MarkupSettings | null
): MinAvailabilityItem {
  if (!markup) return minItem;

  const updatedPricing: MinAvailabilityItem["pricing"] = {};

  // Process each date in the pricing object
  for (const [date, dayPricing] of Object.entries(minItem.pricing)) {
    updatedPricing[date] = applyMarkupToDayPricing(dayPricing, markup);
  }

  return {
    ...minItem,
    pricing: updatedPricing,
  };
}

/**
 * Apply markup to DayRoomAvailability pricing structure
 */
export function applyMarkupToDayRoomAvailability(
  roomAvailability: DayRoomAvailability,
  markup: MarkupSettings | null
): DayRoomAvailability {
  if (!markup) return roomAvailability;

  return {
    ...roomAvailability,
    pricing: applyMarkupToDayPricing(roomAvailability.pricing, markup),
  };
}

/**
 * Apply markup to DayAvailability structure
 */
export function applyMarkupToDayAvailability(
  dayAvailability: DayAvailability,
  markup: MarkupSettings | null
): DayAvailability {
  if (!markup) return dayAvailability;

  return {
    ...dayAvailability,
    rooms: dayAvailability.rooms.map((room) =>
      applyMarkupToDayRoomAvailability(room, markup)
    ),
  };
}

/**
 * Apply markup to BatchAvailabilityResponse
 */
export function applyMarkupToBatchAvailability(
  batchData: BatchAvailabilityResponse,
  markup: MarkupSettings | null
): BatchAvailabilityResponse {
  if (!markup || !batchData) return batchData;

  // Apply markup to minAvailability
  const updatedMinAvailability: BatchAvailabilityResponse["minAvailability"] =
    {};
  for (const [roomId, minItem] of Object.entries(batchData.minAvailability)) {
    updatedMinAvailability[roomId] = applyMarkupToMinAvailabilityItem(
      minItem,
      markup
    );
  }

  // Apply markup to daily data
  const updatedDays = batchData.days.map((day) =>
    applyMarkupToDayAvailability(day, markup)
  );

  return {
    ...batchData,
    minAvailability: updatedMinAvailability,
    days: updatedDays,
  };
}

/**
 * Apply markup to unit price (for room selection)
 */
export function applyMarkupToUnitPrice(
  unitPrice: number,
  markup: MarkupSettings | null
): { unitPrice: number; originalUnitPrice?: number } {
  if (!markup) {
    return { unitPrice };
  }

  const originalUnitPrice = unitPrice;
  const newUnitPrice = calculatePriceWithMarkup(originalUnitPrice, markup);

  return {
    unitPrice: newUnitPrice,
    originalUnitPrice,
  };
}

/**
 * Helper function to get markup-adjusted price from MinAvailabilityItem
 */
export function getPriceFromMinAvailability(
  minItem: MinAvailabilityItem,
  date: string,
  priceType: "single" | "double" | "triple"
): { net: number; gross: number; originalNet?: number } | undefined {
  return minItem.pricing[date]?.[priceType];
}

/**
 * Helper function to check if markup is applied to pricing
 */
export function hasMarkupApplied(
  priceObj: { net: number; gross: number; originalNet?: number } | undefined
): boolean {
  return priceObj?.originalNet !== undefined;
}
